import angular from 'angular';

const HORIZONTAL_SCROLL_SENSITIVITY = 50;
const HORIZONTAL_SCROLL_STEP = 10;

export default function draggableColumns(Utils, $window, $document, $parse, $rootScope) {
  'ngInject';

  const clones = [];
  const moveListeners = [];
  const upListeners = [];
  const stylesheets = [];
  const windowElement = angular.element($window);

  windowElement.on('mouseup', () => {
    clones.forEach((c) => c.remove());
    clones.length = 0;

    moveListeners.forEach((listener) => {
      windowElement.off('mousemove', listener);
    });
    moveListeners.length = 0;

    upListeners.forEach((listener) => {
      windowElement.off('mouseup', listener);
    });
    upListeners.length = 0;

    stylesheets.forEach((s) => s.remove());
    stylesheets.length = 0;
  });

  return {
    restrict: 'A',
    link($scope, $element, $attrs) {
      const isActiveExpression = $parse($attrs.isActive);
      const isLeftAnchored = $scope.$eval($attrs.isLeftAnchored);
      const leftOffset = $scope.$eval($attrs.leftOffset);
      const onChangeParsed = $parse($attrs.onChange);

      $element.on('mousedown', (e) => {
        const th = angular.element(e.target).closest('th[draggable-columns-item]');
        if (!th.length) {
          return;
        }

        if (angular.element(e.target).closest('a').length) {
          return; // clicked on an anchor
        }

        const thRect = th[0].getBoundingClientRect();
        createClones($element, thRect, isLeftAnchored);

        const container = $element[0].querySelector('[draggable-columns-container]');
        const containerRect = container.getBoundingClientRect();

        const anchorOffset = isLeftAnchored ? 1 : 0;

        const stylesheet = $document[0].createElement('style');
        $document[0].head.appendChild(stylesheet);
        stylesheets.push(stylesheet);

        const state = {
          columnIndex: th[0].cellIndex - leftOffset - anchorOffset,
          columnOffset: leftOffset + anchorOffset,
          movedBy: 0,
          scrolledBy: 0,
          array: $scope.$eval($attrs.draggableColumns),
          isActiveColumn: (prop) => isActiveExpression($scope, { $column: prop }),
          stylesheet,
        };

        updateStyle(stylesheet, th[0].cellIndex);

        const listener = getMoveListener({
          thRect,
          mousedownEvent: e,
          container,
          containerRect,
          initialContainerScroll: container.scrollLeft,
          state,
        });

        const initialIndex = getActiveIndexes(state)[state.columnIndex];

        moveListeners.push(listener);
        windowElement.on('mousemove', listener);

        $element.addClass('dragging');

        const mouseUpListener = () => {
          const finalIndex = getActiveIndexes(state)[state.columnIndex];

          if (initialIndex !== finalIndex) {
            $scope.$evalAsync(() => onChangeParsed($scope));
          }

          $element.removeClass('dragging');
        };

        upListeners.push(mouseUpListener);
        windowElement.on('mouseup', mouseUpListener);
      });
    },
  };

  function createClones($element, thRect, isLeftAnchored) {
    Utils.arrayLikeToArray($element[0].querySelectorAll('[draggable-columns-container]')).forEach(
      (e) => layoutClone(thRect, e, isLeftAnchored)
    );
  }

  function layoutClone(thRect, cont, isLeftAnchored) {
    if (!cont) {
      return;
    }

    const container = angular.element(cont);
    const clone = container.clone();
    clones.push(clone);
    clone.addClass('draggable-columns-clone');

    if (isLeftAnchored) {
      clone.addClass('is-left-anchored');
    }

    container.after(clone);

    const containerRect = container[0].getBoundingClientRect();

    clone.css({
      width: `${thRect.width}px`,
      left: `${thRect.left}px`,
      top: `${thRect.top}px`,
      overflow: 'hidden',
    });

    clone.find('table').css({
      /**
       * setting a fixed width fucks with table-layout: fixed for whatever reason
       */
      'max-width': `${container.find('table')[0].offsetWidth}px`,
      'min-width': `${container.find('table')[0].offsetWidth}px`,
    });

    const cloneRect = clone[0].getBoundingClientRect();

    if (cloneRect.bottom > containerRect.bottom) {
      clone.css({
        height: `${cloneRect.height - cloneRect.bottom + containerRect.bottom}px`,
      });

      clone[0].scrollTop = cloneRect.bottom - containerRect.bottom;
    }

    const scroll = container[0].scrollLeft + thRect.left - containerRect.left;
    clone[0].scrollLeft = scroll;
  }

  function getMoveListener({
    thRect,
    mousedownEvent,
    container,
    containerRect,
    initialContainerScroll,
    state,
  }) {
    const firstTh = container.querySelector('th:first-child');

    return (moveEvent) => {
      clones.forEach((clone) => {
        clone.css({
          left: `${thRect.left + moveEvent.clientX - mousedownEvent.clientX}px`,

          // not sure if we should lock 'top' or not: QA will tell
          // top: `${thRect.top + moveEvent.clientY - mousedownEvent.clientY}px`,
        });
      });

      const scrollCorrection = container.scrollLeft - initialContainerScroll;
      handleMovement(
        thRect,
        moveEvent.clientX - mousedownEvent.clientX + state.scrolledBy + scrollCorrection,
        state
      );
      handleScroll(moveEvent, container, containerRect, firstTh);
    };
  }

  function handleMovement(thRect, delta, state) {
    const normalizedDelta = delta - state.movedBy * thRect.width;
    const halfColWidth = thRect.width / 2;

    if (normalizedDelta > halfColWidth) {
      reorder(Math.ceil((normalizedDelta / halfColWidth - 1) / 2), state);
    } else if (normalizedDelta < -halfColWidth) {
      reorder(-Math.ceil(-(normalizedDelta / halfColWidth + 1) / 2), state);
    }
  }

  function handleScroll(event, container, containerRect, firstTh) {
    if (containerRect.right - event.clientX < HORIZONTAL_SCROLL_SENSITIVITY) {
      container.scrollLeft += HORIZONTAL_SCROLL_STEP;
    }

    if (
      event.clientX - containerRect.left - firstTh.getBoundingClientRect().width <
      HORIZONTAL_SCROLL_SENSITIVITY
    ) {
      container.scrollLeft -= HORIZONTAL_SCROLL_STEP;
    }
  }

  function getActiveIndexes(state) {
    return state.array
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.active && state.isActiveColumn(item.property))
      .map(({ index }) => index);
  }

  function reorder(d, state) {
    const activeIndexes = getActiveIndexes(state);
    const currentIndex = activeIndexes[state.columnIndex];
    const item = state.array[currentIndex];

    if (state.columnIndex + d < 0 || state.columnIndex + d > activeIndexes.length - 1) {
      return;
    }

    state.movedBy += d;

    state.array.splice(currentIndex, 1);
    state.columnIndex += d;
    const newIndex = activeIndexes[state.columnIndex];
    state.array.splice(newIndex, 0, item);

    $rootScope.$$postDigest(() => {
      updateStyle(state.stylesheet, state.columnIndex + state.columnOffset);
    });
    $rootScope.$evalAsync(); // make sure a digest is run eventually
  }
}

function updateStyle(stylesheet, index) {
  const prefix =
    '[draggable-columns].dragging [draggable-columns-container]:not(.draggable-columns-clone)';
  stylesheet.innerHTML = `
    ${prefix} th:nth-child(${index + 1}),
    ${prefix} td:nth-child(${index + 1}) {
      opacity: 0;
    }
  `;
}
